import { useState, type ReactNode } from "react";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";
import { Download, Loader2, PenLine, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { formatBRL, formatDate } from "./prosthesis-meta";
import { DENTIST_STATUS_META, dentistStatusBucket } from "./dentist-status";
import { RequestReworkDialog, type ReworkTarget } from "./RequestReworkDialog";
import { SentChannelsField } from "./SentChannelsField";
import type { ProsthesisOrderRow, ProsthesisServiceRow } from "./types";
import { LinkDoPaciente } from "./LinkDoPaciente";
import { AlteracoesDoServico, type Alteracao } from "./AlteracoesDoServico";
import { totalParaExibir } from "@/components/prosthesis/valor-do-pedido";
import { AcrescimosDoPedido } from "./AcrescimosDoPedido";
import { CartaoDoServico } from "./CartaoDoServico";
import { PilulaDeStatus } from "./PilulaDeStatus";
import { PrazoDoPedido } from "./PrazoDoPedido";

async function downloadAttachment(utils: ReturnType<typeof trpc.useUtils>, fileId: string) {
  try {
    const { url } = await utils.file.getDownloadUrl.fetch({ fileId });
    window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    toast.error("Não foi possível baixar o arquivo.");
  }
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">{label}</p>
      <p className="text-sm text-zinc-900 dark:text-foreground">{value}</p>
    </div>
  );
}

export function ProsthesisOrderDetailDialog({
  orderId,
  onClose,
  readOnly = false,
  onEditar,
  aviso,
}: {
  orderId: string | null;
  onClose: () => void;
  /** Resumo do Início — só mostra o pedido, sem "Enviado também por" editável nem "Solicitar
   *  retrabalho". A edição completa continua em /protese e /agenda (default). */
  readOnly?: boolean;
  /** Quem hospeda o formulário de pedido oferece a edição por aqui; sem isso o botão não aparece
   *  (o diálogo sozinho não tem onde abrir o formulário). */
  onEditar?: (order: ProsthesisOrderRow) => void;
  /** Faixa logo abaixo do título — o "pedido de outra organização, só leitura" do acesso pessoal. */
  aviso?: ReactNode;
}) {
  const [reworkTarget, setReworkTarget] = useState<ReworkTarget>(null);
  // Em foco de outra organização o link da ficha abriria a ativa — o nome fica só texto.
  const emOutra = useEmOutraOrganizacao();
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const podeGerenciar = usePermissao("prosthesis.manage");
  const orderQuery = trpc.prosthesis.getById.useQuery({ id: orderId ?? "" }, { enabled: !!orderId });
  const order = orderQuery.data as ProsthesisOrderRow | undefined;
  const alteracoesQuery = trpc.prosthesis.changes.listByOrder.useQuery({ orderId: orderId ?? "" }, { enabled: !!orderId });
  const alteracoes = (alteracoesQuery.data ?? []) as Alteracao[];

  const remover = trpc.prosthesis.remove.useMutation({
    onSuccess: () => {
      toast.success("Pedido excluído.");
      utils.prosthesis.list.invalidate();
      utils.prosthesis.countByStatus.invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.message || "Não foi possível excluir o pedido."),
  });

  async function excluir() {
    if (!order) return;
    const ok = await confirm({
      title: "Excluir este pedido?",
      description:
        order.status === "AGUARDANDO_CONFIRMACAO"
          ? "O laboratório ainda não confirmou — o pedido some da fila dele e nada fica registrado."
          : "O pedido será removido da sua lista. Essa ação não pode ser desfeita.",
      confirmText: "Excluir pedido",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (ok) remover.mutate({ id: order.id });
  }

  // A mesma regra do servidor: editar só antes do aceite; excluir antes do aceite ou depois que o
  // laboratório desistiu. Entre o aceite e a entrega, o pedido é trabalho na bancada de outra
  // organização — a clínica pede retrabalho, não reescreve.
  const podeEditar = !readOnly && podeGerenciar && !!onEditar && order?.status === "AGUARDANDO_CONFIRMACAO";
  const podeExcluir =
    !readOnly && podeGerenciar && !!order && ["AGUARDANDO_CONFIRMACAO", "RECUSADO", "CANCELADO"].includes(order.status);

  return (
    <>
      <Dialog open={!!orderId} onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0 outline-none focus-visible:outline-none dark:bg-card"
          // Sem isso o foco caía no X (o único botão enquanto carrega) e ele abria destacado.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {orderQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : order ? (
            <>
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
              {/* Mesmo desenho do detalhe do laboratório: nome e situação em cima, número embaixo. Tudo
                  numa linha só quebrava "Pedido nº" longe do número no celular. */}
              <DialogHeader className="text-left">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pr-8">
                  <DialogTitle ajuda={AJUDA_PROTESE.detalheDoPedido}>
                    {emOutra ? order.patientName : <LinkDoPaciente patientId={order.patientId} patientName={order.patientName} onAbrir={onClose} />}
                  </DialogTitle>
                  <PilulaDeStatus
                    rotulo={DENTIST_STATUS_META[dentistStatusBucket(order.status)].label}
                    cor={DENTIST_STATUS_META[dentistStatusBucket(order.status)].color}
                    fundo={DENTIST_STATUS_META[dentistStatusBucket(order.status)].bg}
                  />
                </div>
                {(order.numero || order.itemDoPlano) && (
                  <p className="text-sm text-zinc-500 dark:text-muted-foreground">
                    {order.numero ? `Pedido nº ${order.numero}` : null}
                    {order.numero && order.itemDoPlano ? " · " : null}
                    {order.itemDoPlano ? (
                      <span className="font-medium text-brand-700 dark:text-primary">Do plano de tratamento nº {order.itemDoPlano.plano.numero}</span>
                    ) : null}
                  </p>
                )}
              </DialogHeader>
              {aviso}

              {/* Três colunas iguais: Laboratório e Dentista ficam sobre Data e Prazo. */}
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-zinc-50 p-3 text-sm dark:bg-background/50 sm:grid-cols-3">
                <Field label="Laboratório" value={order.labName ?? "Sem laboratório definido"} className="col-span-2 sm:col-span-1" />
                <Field label="Dentista" value={order.dentistName ?? "—"} className="col-span-2 sm:col-span-1" />
                <Field label="Data do pedido" value={formatDate(order.createdAt)} className="sm:col-start-1" />
                <PrazoDoPedido deadline={order.deadline} alteracoes={(order as any).alteracoes} />
                <Field
                  className="col-span-2 sm:col-span-1"
                  label="Valor total"
                  value={formatBRL(totalParaExibir(order.services, order.acrescimos))}
                />
              </div>

              {order.status === "RECUSADO" && order.declineReason && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 dark:border-red-900/40 dark:bg-red-950/10">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
                    Motivo da recusa
                  </p>
                  <p className="text-sm text-zinc-700 dark:text-foreground/80">{order.declineReason}</p>
                </div>
              )}

              {order.description && (
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                    Observações gerais
                  </p>
                  <p className="text-sm text-zinc-700 dark:text-foreground/80">{order.description}</p>
                </div>
              )}

              <SentChannelsField orderId={order.id} sentChannels={order.sentChannels} readOnly={readOnly} />

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
                  Serviços ({order.services.length})
                </p>
                <div className="flex flex-col gap-2">
                  {order.services.map((service) => (
                    <ServiceRow
                      key={service.id}
                      service={service}
                      alteracoes={alteracoes.filter((a) => a.serviceId === service.id)}
                      readOnly={readOnly}
                      onRequestRework={() =>
                        setReworkTarget({
                          serviceId: service.id,
                          serviceLabel: service.serviceCategory.label,
                          patientName: order.patientName,
                        })
                      }
                    />
                  ))}
                </div>
              </div>

              <AcrescimosDoPedido orderId={order.id} acrescimos={order.acrescimos ?? []} podeEditar={false} />
              </div>
            {(podeEditar || podeExcluir) && (
              <div className="flex flex-wrap items-center gap-2 border-t border-zinc-200 px-6 py-3 dark:border-border">
                {podeEditar && (
                  <button
                    type="button"
                    onClick={() => onEditar!(order)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
                  >
                    <PenLine className="h-3.5 w-3.5" />
                    Editar pedido
                  </button>
                )}
                {podeExcluir && (
                  <button
                    type="button"
                    disabled={remover.isPending}
                    onClick={excluir}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:opacity-60 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-950/20"
                  >
                    {remover.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Excluir pedido
                  </button>
                )}
                {podeEditar && (
                  <span className="mr-auto order-first text-[11.5px] text-zinc-500 dark:text-muted-foreground">
                    Disponível até o laboratório confirmar.
                  </span>
                )}
              </div>
            )}

            </>
          ) : (
            <p className="p-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">Pedido não encontrado.</p>
          )}
        </DialogContent>
      </Dialog>

      <RequestReworkDialog target={reworkTarget} onClose={() => setReworkTarget(null)} />
    </>
  );
}

/** Mesmo layout do serviço usado em /lab/producao (ProductionOrderDetailDialog) — odontograma
 *  ao lado dos campos — só que aqui o status é só leitura (quem avança a produção é o
 *  laboratório) e tem o botão de solicitar retrabalho no lugar do download de anexo. */
function ServiceRow({
  service,
  alteracoes,
  onRequestRework,
  readOnly = false,
}: {
  service: ProsthesisServiceRow;
  alteracoes: Alteracao[];
  onRequestRework: () => void;
  readOnly?: boolean;
}) {
  const utils = trpc.useUtils();
  // Dentista vê só o status simplificado (mesmo bucket usado na lista/cards de Prótese) — os 15
  // status granulares do laboratório (Vazar Gesso, Fresando/Imprimindo...) não fazem sentido aqui.
  const statusMeta = DENTIST_STATUS_META[dentistStatusBucket(service.status)];
  // Só faz sentido pedir retrabalho depois que o laboratório entregou — é aí que o dentista
  // consegue avaliar se o serviço saiu certo ou precisa ser refeito. Checagem contra o status
  // granular real (não o bucket), continua igual. Nunca no modo readOnly (resumo do Início).
  const canRequestRework = !readOnly && service.status === "CONCLUIDO";

  return (
    <CartaoDoServico
      service={service}
      status={<PilulaDeStatus rotulo={statusMeta.label} cor={statusMeta.color} fundo={statusMeta.bg} alerta={service.hasOpenRework} />}
      extra={<AlteracoesDoServico alteracoes={alteracoes} />}
      acoes={
        service.attachmentFile || canRequestRework ? (
          <>
            {service.attachmentFile && (
              <button
                type="button"
                onClick={() => downloadAttachment(utils, service.attachmentFile!.id)}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                <Download className="h-3.5 w-3.5" />
                Baixar anexo
              </button>
            )}
            {canRequestRework && (
              <button
                type="button"
                onClick={onRequestRework}
                className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[12px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Solicitar retrabalho
              </button>
            )}
          </>
        ) : undefined
      }
    />
  );
}
